<template>
  <div class="message-details-wrapper">
    <AlertErrorMsg
      ref="alert"
      :title="errorMessage"
      class="page-alert-error"
    />
    <div class="message-details-header">
      <h2 class="message-details-title">
        Details
      </h2>
      <WhiteCloseIcon28 @click="closeDetails()" />
    </div>
    <div class="menu-item-wrapper">
      <div class="menu-item-header">
        <div class="header-text">
          <h2 class="menu-item-title">
            Tags
          </h2>
          <span v-if="hasPermission(['add_tag'])" @click="$refs.editTagsPopup.openPopups()">Edit</span>
        </div>
        <GreyArrowUpIcon24
          :style="{ transform: isViewTags ? '' : 'rotate(180deg)' }"
          @click="isViewTags = !isViewTags"
        />
      </div>
      <transition v-if="isViewTags" name="slide">
        <div class="tags-wrapper" :class="{ 'tags-disabled': isLoadingMessages || isLoadingMedia }">
          <div v-for="(category, index) in chatTags" :key="index" class="tag-section">
            <h2 class="section-header">
              {{ category.category.name }}
            </h2>
            <div class="section-tags">
              <div v-for="(tag, i) in category.tags" :key="i" class="tag">
                <div class="check">
                  <CustomCheckbox
                    :id="tag.id"
                    :input-element="tag"
                    @checked="changeTagState"
                  />
                </div>
                <span>{{ tag.tag }}</span>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
    <EditTagsPopup
      ref="editTagsPopup"
      :tags-list="tagsList"
      @addTag="$emit('addTag', $event)"
      @deleteTag="$emit('deleteTag', $event)"
      @changeTag="$emit('changeTag', $event)"
      @addNewCategory="$emit('addNewCategory', $event)"
      @changeCategory="$emit('changeCategory', $event)"
      @deleteCategory="$emit('deleteCategory', $event)"
      @changeTagOrder="$emit('changeTagOrder', $event)"
    />
  </div>
</template>

<script>
import CustomCheckbox from '@/components/inputs/CustomCheckbox.vue';
import parseDate from '@/mixins/parseDate';
import parseDateTime from '@/mixins/parseTime';
import AlertErrorMsg from '@/components/profile/AlertErrorMsg.vue';
import EditTagsPopup from '@/components/popups/EditTagsPopup.vue';
import hasPermission from '@/utils/permission';
import {mapGetters} from 'vuex';
import debounce from '@/mixins/debounce';
import WhiteCloseIcon28 from '@/components/icons/WhiteCloseIcon28.vue';
import GreyArrowUpIcon24 from '@/components/icons/GreyArrowUpIcon24.vue';

export default {
  name: 'MessagesTagsList',
  components: {
    GreyArrowUpIcon24,
    WhiteCloseIcon28,
    EditTagsPopup,
    AlertErrorMsg,
    CustomCheckbox,
  },
  emits: [
    'tagsUpdated',
    'changeTag',
    'changeTagOrder',
    'addTag',
    'deleteTag',
    'deleteCategory',
    'changeCategory',
    'addNewCategory',
  ],
  mixins: [parseDate, parseDateTime, debounce],
  props: {
    tagsList: {
      type: Array,
      default: () => [],
    },
    chatTags: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      errorMessage: '',
      isViewTags: true,
    };
  },
  computed: {
    ...mapGetters('messagesTagging', {
      isLoadingMessages: 'getLoadingState',
    }),
    ...mapGetters('mediaTagging', {
      taggingItem: 'getTaggingItem',
      isLoadingMedia: 'isLoading',
    }),
  },
  methods: {
    hasPermission,
    closeDetails() {
      this.$router.push({name: 'Case messages mobile chats'});
    },
    changeTagState() {
      this.$emit('tagsUpdated');
    },
  },
};
</script>
<style scoped lang="scss">
.message-details-wrapper {
  max-width: 435px;
  max-height: 1031px;
  width: 100%;
  height: 85vh;
  overflow-y: scroll;
  overflow-x: hidden;
  border-left: 1px solid var(--border-color);

  .message-details-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 25px;

    .message-details-title {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      line-height: 21.78px;
      color: var(--secondary-black-color);
    }
  }

  .menu-item-wrapper {

    .menu-item-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 25px;
      background-color: var(--hover-accent-color);

      .header-text {
        display: flex;
        align-items: center;
        gap: 10px;

        span {
          font-size: 14px;
          font-weight: 400;
          line-height: 16.94px;
          text-align: left;
          color: var(--light-gray);
          cursor: pointer;
        }
      }

      .menu-item-title {
        font-size: 14px;
        font-weight: 700;
        line-height: 21px;
        text-align: left;
        text-transform: uppercase;
        color: var(--secondary-black-color);
      }

      svg {
        transition: transform 0.3s;
      }
    }
  }
}

.message-text {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 15px 25px 25px;
  transition: all .5s;
  position: relative;

  p {
    font-size: 14px;
    font-weight: 500;
    line-height: 19.2px;
    text-align: left;
    color: var(--black-color);
  }
}

.tags-wrapper {
  padding: 0 25px 15px;
  transition: all .5s;

  .tag-section {
    padding-top: 15px;

    .section-header {
      font-size: 16px;
      font-weight: 700;
      line-height: 19.36px;
      text-align: left;
      color: var(--accent-color);
      padding-bottom: 15px;
    }

    .section-tags {

      .tag {
        display: flex;
        gap: 10px;
      }
    }

    &:not(:last-child) {
      padding-bottom: 15px;
      border-bottom: 1px solid var(--border-color);
    }
  }
}

.tags-disabled {
  opacity: 0.4;
  pointer-events: none;
}

h2 {
  margin: 0;
}

svg {
  cursor: pointer;
}

.slide-enter-active, .slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter, .slide-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

.slide-enter-to, .slide-leave {
  max-height: 1000px;
  opacity: 1;
}

.tools {
  position: absolute;
  z-index: 2;
  top: 50%;
  right: 0;
  justify-content: center;
  align-items: center;
  transition: 0.2s all;
  border: 1px solid var(--case-btn-border-color);
  border-radius: 8px;
  padding: 8px;
  background-color: var(--white-color);
  font-size: 12px;
}

.colors {
  display: flex;
  align-items: center;
  transform: translate(-10%, -50%);
  gap: 16px;

  .close {
    border: none;
    width: fit-content;
    padding: 0;
    background-color: transparent;
  }

  .color {
    border: 1px solid transparent;
    border-radius: 50%;
    width: 16px;
    height: 16px;

    &.white {
      border-color: var(--case-btn-border-color);
    }

    &:hover,
    &.active {
      border-color: #373c44;
    }
  }
}

.underline {
  text-decoration: underline;
  text-underline-offset: 2px;
}

.background {
  position: absolute;
  z-index: -1;
  top: 15px;
  right: 50px;
  bottom: 30px;
  left: 20px;
  opacity: 0.4;
}

</style>
